/**
 * CR-6440: what the Forecast tab's Refresh button does.
 *
 * Refresh is always available, as on the Dashboard page (DashboardFilterBar →
 * fetchDashboardData(true)). Snapshot installs trigger the on-demand snapshot
 * rebuild and reload the grid once it lands (`useForecastSnapshotRefresh`);
 * every other tenant re-fetches the grid with the applied filters. Before this
 * the button rendered only when the snapshot was enabled, so most tenants had
 * no Refresh on either the customer or the supplier view.
 */
import { useCallback } from 'react';
import { ForecastRefreshStatus, useForecastSnapshotRefresh } from './useForecastSnapshotRefresh';

export interface ForecastRefresh {
  refreshStatus: ForecastRefreshStatus | null;
  /** True while a snapshot rebuild is being polled. */
  refreshing: boolean;
  /** Click handler for the Refresh button. */
  handleRefresh: () => void;
  /** Tooltip for the Refresh button. */
  refreshTitle: string;
}

/**
 * Resolves the Refresh button's behaviour for the current install.
 *
 * @param reload - re-fetches the grid with the applied filters
 * @returns snapshot status, the in-flight flag, the click handler and its tooltip
 * @example
 * const { refreshStatus, refreshing, handleRefresh, refreshTitle } = useForecastRefresh(reload);
 */
export function useForecastRefresh(reload: () => Promise<void>): ForecastRefresh {
  const { refreshStatus, refreshing, handleRefreshSnapshot } = useForecastSnapshotRefresh(reload);
  const snapshotEnabled = Boolean(refreshStatus?.enabled);

  const handleRefresh = useCallback(() => {
    if (snapshotEnabled) void handleRefreshSnapshot();
    else void reload();
  }, [snapshotEnabled, handleRefreshSnapshot, reload]);

  const refreshTitle = snapshotEnabled
    ? (refreshStatus?.last_refreshed_at
      ? `Last refreshed ${new Date(refreshStatus.last_refreshed_at).toLocaleString()}`
      : 'Trigger an on-demand snapshot refresh')
    : 'Reload the forecast schedule';

  return { refreshStatus, refreshing, handleRefresh, refreshTitle };
}
